import type { MessageCode } from '../messages/vi';

/** The five sections of the app shell, in the order of the header nav and the tab bar. */
export const NAV_TABS = ['home', 'theory', 'exam', 'practice', 'me'] as const;
export type NavTab = (typeof NAV_TABS)[number];

export interface NavItem {
  readonly id: NavTab;
  readonly to: string;
  readonly label: MessageCode;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', to: '/', label: 'nav.home' },
  { id: 'theory', to: '/ly-thuyet', label: 'nav.theory' },
  { id: 'exam', to: '/thi-thu', label: 'nav.exam' },
  { id: 'practice', to: '/sa-hinh', label: 'nav.practice' },
  { id: 'me', to: '/toi', label: 'nav.me' },
];

/**
 * `?hang=` values of the theory route. The shell cannot import `CLASS_SLUGS` from
 * `features/theory/route-params.ts`: that module brings `@dcs/theory-core` and the regulation data,
 * which `/` must not load (budgets.json `app.routes["/"]`). `nav.test.ts` keeps the two lists equal.
 */
export const THEORY_CLASS_SLUGS = ['b', 'c1', 'c', 'd'] as const;
const THEORY_CLASS_SLUG_SET: ReadonlySet<string> = new Set(THEORY_CLASS_SLUGS);

/**
 * `/ly-thuyet` is a mock exam when `?hang=` names a class and there is no `?ontap=` (the rule of
 * `readView` in `features/theory/route-params.ts`); everything else there is practice.
 */
function isTheoryExam(query: URLSearchParams): boolean {
  const hang = query.get('hang')?.toLowerCase() ?? '';
  return THEORY_CLASS_SLUG_SET.has(hang) && query.get('ontap') === null;
}

/** `pathname` is `prefix` or a sub path of it. */
const under = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const ME_PATHS = ['/toi', '/lich-su', '/tai-khoan', '/dang-nhap'] as const;
const PRACTICE_PATHS = ['/sa-hinh', '/drive', '/lam-quen'] as const;

/**
 * Which tab is "the current page" (design D-4): Lý thuyết for `/ly-thuyet` in practice; Thi thử for
 * `/thi-thu`, a theory mock exam (`/ly-thuyet?hang=…` without `ontap`) and `/exam` without `bai`;
 * Sa hình 3D for `/sa-hinh`, `/drive`, `/exam?bai=…` and `/lam-quen`; Tôi for `/toi` and the
 * account screens (`/lich-su[/:id]`, `/tai-khoan`, `/dang-nhap`). Any other path (404): none.
 */
export function activeTab(pathname: string, search: string): NavTab | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const query = new URLSearchParams(search);
  if (path === '/' || path === '') return 'home';
  if (under(path, '/ly-thuyet')) return isTheoryExam(query) ? 'exam' : 'theory';
  if (under(path, '/thi-thu')) return 'exam';
  if (under(path, '/exam')) return query.get('bai') === null ? 'exam' : 'practice';
  if (PRACTICE_PATHS.some((prefix) => under(path, prefix))) return 'practice';
  if (ME_PATHS.some((prefix) => under(path, prefix))) return 'me';
  return null;
}
